import React, { useState, useEffect } from 'react';
import { 
  Box, Button, TextField, Snackbar, Alert, Paper, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  TablePagination,
} from '@mui/material';
import axios from 'axios';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface User {
  id: number;
  name: string;
}

interface StoredData {
  users: User[];
  timestamp: number;
  date: string;
  checkType: 'missing' | 'filled';
}

const CheckUsersPerDay: React.FC = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  const [missingUsers, setMissingUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    yesterday.toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkType, setCheckType] = useState<'missing' | 'filled'>('missing');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const storedDataStr = localStorage.getItem('userCheckData');
    if (storedDataStr) {
      const storedData: StoredData = JSON.parse(storedDataStr);
      const currentTime = Date.now();
      
      if (currentTime - storedData.timestamp < 600000) {
        setMissingUsers(storedData.users);
        setCheckType(storedData.checkType);
        setSelectedDate(storedData.date);
        setSnackbar({
          open: true,
          message: 'Данные загружены из предыдущего сеанса',
          severity: 'info',
        });
      }
    }
  }, []);

  useEffect(() => {
    document.title = 'Список пользователей без данных | Zerde';
  }, []);

  useEffect(() => {
    const filtered = missingUsers.filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
    setPage(0);
  }, [searchTerm, missingUsers]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const handleCheck = async (type: 'missing' | 'filled') => {
    if (!selectedDate) {
      setSnackbar({
        open: true,
        message: 'Не выбрана дата!',
        severity: 'error',
      });
      return;
    }

    setCheckType(type);
    setLoading(true);
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/check-users', {
        params: { 
          date: selectedDate,
          checkType: type
        }
      });
      const usersWithIds = response.data.users.map((name: string, index: number) => ({ id: index + 1, name }));
      
      const storageData: StoredData = {
        users: usersWithIds,
        timestamp: Date.now(),
        date: selectedDate,
        checkType: type
      };
      localStorage.setItem('userCheckData', JSON.stringify(storageData));
      
      setMissingUsers(usersWithIds);
      setSnackbar({
        open: true,
        message: `Найдено ${usersWithIds.length} пользователей.`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при проверке пользователей: ' + error,
        severity: 'error',
      });
      console.error('Ошибка при проверке пользователей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box >
      <Box sx={{ display: 'flex', marginBottom: 2, gap: 2, alignItems: 'flex-end'}}>
        <TextField
          size='small'
          label="Выберите дату"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={selectedDate}
          onChange={handleDateChange}
        />
        <Button 
          variant="contained" 
          color="error" 
          onClick={() => handleCheck('missing')}
          disabled={loading}
          sx={{mt: 2, minWidth: 150}}
        >
          {loading && checkType === 'missing' ? 'Загрузка...' : 'Не Заполнено'}
        </Button>

        <Button 
          variant="contained" 
          color="success" 
          onClick={() => handleCheck('filled')}
          disabled={loading}
          sx={{mt: 2, minWidth: '150px'}}
        >
          {loading && checkType === 'filled' ? 'Загрузка...' : 'Заполнено'}
        </Button>
      </Box>

      {filteredUsers.length > 0 && (
        <Paper sx={{ marginTop: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>Имя пользователя</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.name}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckUsersPerDay;